/** Desktop search with real visited places, scoped session queries and page filters. */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Clock, Search, SlidersHorizontal, Sparkles, X } from '@/shared/ui/icons';
import { Button } from '@/shared/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/shared/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { addSubmittedSearch } from './desktopHeaderSearchHistory';
import { isQuestionLike, searchEnterAction } from '../search/askRouting';
import { primaryModifierKeyLabel } from '../../utils/keyboardModifier';

export interface DesktopHeaderSearchPlace {
  id: string;
  label: string;
  description?: string;
  groupLabel?: string;
  icon?: ReactNode;
  onSelect: () => void | Promise<boolean>;
}
export interface DesktopHeaderSearchFilter {
  id: string;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onValueChange: (value: string) => void;
}
export interface DesktopHeaderSearchProps {
  scopeKey: string;
  scopeLabel: string;
  value: string;
  inputRef: RefObject<HTMLInputElement | null>;
  onValueChange: (value: string) => void;
  onClear: () => void;
  placeholder: string;
  ariaLabel: string;
  searchScope?: string;
  filters: DesktopHeaderSearchFilter[];
  recentPlaces?: DesktopHeaderSearchPlace[];
  /** Already matched records from the current page, in relevance order. */
  results?: DesktopHeaderSearchPlace[];
  /** Views without a local query search the shell's authorized destinations. */
  destinations?: DesktopHeaderSearchPlace[];
  globalSearch?: boolean;
  resultsLoading?: boolean;
  resultsMessage?: string;
  moreResults?: Array<{ id: string; label: string; onSelect: () => void; disabled?: boolean }>;
  onOpenChange?: (open: boolean) => void;
  /** Offers the typed text to QuantisAI from a row at the top of the results. */
  onAsk?: (text: string) => void;
}
type Suggestion = { kind: 'result' | 'place' | 'destination'; place: DesktopHeaderSearchPlace } | { kind: 'query'; query: string; current: boolean } | { kind: 'ask'; query: string };
const suggestionGroup = (suggestion: Suggestion, hasQuery: boolean) => suggestion.kind === 'ask' ? 'QuantisAI' : suggestion.kind === 'result'
  ? suggestion.place.groupLabel ?? 'Matching results'
  : suggestion.kind === 'place' ? 'Recent places' : suggestion.kind === 'destination' ? 'Go to'
    : hasQuery ? 'Search this view' : 'Recent searches';

function SearchFilters({ filters }: { filters: DesktopHeaderSearchFilter[] }) {
  return <div className="flex flex-wrap gap-3 border-b border-border px-4 py-3">
    {filters.map(filter => <div key={filter.id} className="min-w-[120px] flex-1">
      <label className="mb-1 block text-xs text-muted-foreground">{filter.label}</label>
      <Select value={String(filter.options.findIndex(option => option.value === filter.value))} onValueChange={index => {
        const option = filter.options[Number(index)];
        if (option) filter.onValueChange(option.value);
      }}>
        <SelectTrigger aria-label={filter.label} className="h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{filter.options.map((option, index) => <SelectItem key={option.value} value={String(index)}>{option.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>)}
  </div>;
}

export function DesktopHeaderSearch({ scopeKey, scopeLabel, value, inputRef, onValueChange, onClear, placeholder, ariaLabel, searchScope, filters, recentPlaces = [], results = [], destinations, globalSearch = false, resultsLoading = false, resultsMessage = '', moreResults = [], onOpenChange, onAsk }: DesktopHeaderSearchProps) {
  // The parent keys by user and workspace; submitted queries never touch storage.
  const histories = useRef(new Map<string, string[]>());
  const [openScope, setOpenScope] = useState<string | null>(null);
  const [filterScope, setFilterScope] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [, refreshHistory] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const suggestionListRef = useRef<HTMLDivElement>(null);
  const dismissedEscape = useRef<Event | null>(null);
  const restoreInputFocus = useRef(false);
  const suppressNextFocus = useRef(false);
  const selectionRequest = useRef(0);
  useLayoutEffect(() => { selectionRequest.current++; }, [scopeKey, value]);
  useEffect(() => () => { selectionRequest.current++; }, []);
  const listId = useId();
  const filterId = useId();
  const open = openScope === scopeKey;
  const [geometry, setGeometry] = useState({ width: 620, height: 36, availableHeight: 560 });
  const measureAnchor = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;
    const next = { width: rect.width, height: rect.height, availableHeight: Math.max(rect.height, Math.min(560, window.innerHeight - rect.top - 16)) };
    setGeometry(previous => previous.width === next.width && previous.height === next.height && previous.availableHeight === next.availableHeight ? previous : next);
  }, []);
  useLayoutEffect(() => {
    if (!open) return;
    measureAnchor();
    const observer = new ResizeObserver(measureAnchor);
    if (anchorRef.current) observer.observe(anchorRef.current);
    window.addEventListener('resize', measureAnchor);
    return () => { observer.disconnect(); window.removeEventListener('resize', measureAnchor); };
  }, [open, measureAnchor]);
  const history = histories.current.get(scopeKey) ?? [];
  const currentQuery = value.trim();
  const normalizedQuery = currentQuery.toLocaleLowerCase();
  const navigationSearch = destinations !== undefined;
  const places = recentPlaces.filter(place => !(navigationSearch && normalizedQuery) && (!normalizedQuery || `${place.label} ${place.description ?? ''}`.toLocaleLowerCase().includes(normalizedQuery)));
  const matchingDestinations = normalizedQuery ? destinations?.filter(place => `${place.label} ${place.description ?? ''}`.toLocaleLowerCase().includes(normalizedQuery)) ?? [] : [];
  const recent = history.filter(query => !normalizedQuery || (query.toLocaleLowerCase().includes(normalizedQuery) && query.toLocaleLowerCase() !== normalizedQuery));
  const askDefault = !!onAsk && isQuestionLike(currentQuery);
  const suggestions: Suggestion[] = [
    ...(onAsk && currentQuery ? [{ kind: 'ask' as const, query: currentQuery }] : []),
    ...(currentQuery ? results.map(place => ({ kind: 'result' as const, place })) : []),
    ...places.map(place => ({ kind: 'place' as const, place })),
    ...matchingDestinations.map(place => ({ kind: 'destination' as const, place })),
    ...(!navigationSearch && currentQuery ? [{ kind: 'query' as const, query: currentQuery, current: true }] : []),
    ...(!navigationSearch ? recent.map(query => ({ kind: 'query' as const, query, current: false })) : []),
  ];
  useEffect(() => {
    if (open && activeIndex >= 0) {
      suggestionListRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  const captureMorph = (direction: 'open' | 'close') => {
    const content = contentRef.current;
    if (!content || content.getAttribute('data-state') !== (direction === 'open' ? 'closed' : 'open')) return;
    const clip = getComputedStyle(content).clipPath;
    const property = `--qs-search-${direction}-from`;
    if (clip && clip !== 'none') content.style.setProperty(property, clip);
    else content.style.removeProperty(property);
  };
  useLayoutEffect(() => {
    if (!open) return;
    const expandedInput = contentRef.current?.querySelector<HTMLInputElement>('input[role="combobox"]');
    // A reversed close reuses the portal after the collapsed field has detached.
    if (expandedInput && inputRef.current !== expandedInput) { inputRef.current = expandedInput; expandedInput.focus(); }
  }, [open, inputRef]);
  const show = () => {
    if (suppressNextFocus.current) { suppressNextFocus.current = false; return; }
    if (!open) { captureMorph('open'); selectionRequest.current++; }
    measureAnchor();
    setOpenScope(scopeKey);
    setFilterScope(null);
    setActiveIndex(-1);
    if (!open) onOpenChange?.(true);
  };
  const close = () => { if (open) captureMorph('close'); selectionRequest.current++; setOpenScope(null); setActiveIndex(-1); onOpenChange?.(false); };
  const submit = (query: string) => {
    restoreInputFocus.current = true;
    onValueChange(query);
    histories.current.set(scopeKey, addSubmittedSearch(history, query));
    refreshHistory(previous => previous + 1);
    close();
  };
  const select = (suggestion: Suggestion) => {
    if (suggestion.kind === 'ask') { restoreInputFocus.current = false; onAsk?.(suggestion.query); onClear(); close(); }
    else if (suggestion.kind === 'query') submit(suggestion.query);
    else {
      restoreInputFocus.current = false;
      const request = ++selectionRequest.current;
      const outcome = suggestion.place.onSelect();
      if (outcome instanceof Promise) void outcome.then(allowed => { if (allowed && request === selectionRequest.current) close(); }).catch(() => { /* The result provider owns its error state. */ });
      else close();
    }
  };
  const isAnchorTarget = (target: EventTarget | null) => target instanceof Node && !!anchorRef.current?.contains(target);
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      const alreadyDismissed = dismissedEscape.current === event.nativeEvent;
      dismissedEscape.current = null;
      if (alreadyDismissed) return;
      if (open) { restoreInputFocus.current = true; close(); } else onClear();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) show();
      setActiveIndex(index => !suggestions.length ? -1 : index < 0 || index >= suggestions.length
        ? (event.key === 'ArrowDown' ? 0 : suggestions.length - 1)
        : (index + (event.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (event.nativeEvent.isComposing) return;
      const forceAsk = event.metaKey || event.ctrlKey;
      if (open && !forceAsk && suggestions[activeIndex]) { select(suggestions[activeIndex]); return; }
      const searchable = suggestions.filter(suggestion => suggestion.kind !== 'ask');
      const action = searchEnterAction({ query: currentQuery, askAvailable: !!onAsk, forceAsk, navigationSearch,
        hasResults: searchable[0]?.kind === 'result' || (navigationSearch && searchable.length > 0) });
      if (action === 'ask') select({ kind: 'ask', query: currentQuery });
      else if (action === 'first-result') select(searchable[0]);
      else if (action === 'submit-search') submit(value);
    }
  };
  const transferInputRef = useCallback((node: HTMLInputElement | null) => {
    if (!node) return;
    inputRef.current = node;
    // A closing portal can detach after the collapsed input has attached.
    return () => { if (inputRef.current === node) inputRef.current = null; };
  }, [inputRef]);
  // Both inputs need search semantics so password managers do not mistake them for a username.
  const renderInput = (expanded: boolean) => <input
    type="search" name="quantis-search" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
    ref={transferInputRef} role="combobox" aria-autocomplete="list" aria-expanded={expanded}
    aria-controls={expanded ? listId : undefined}
    aria-activedescendant={expanded && suggestions[activeIndex] ? `${listId}-${activeIndex}` : undefined}
    aria-label={ariaLabel} placeholder={placeholder} value={value}
    onFocus={expanded ? undefined : show} onClick={expanded ? undefined : show}
    onChange={event => { onValueChange(event.target.value); if (!open) show(); setActiveIndex(-1); }} onKeyDown={handleKeyDown}
    className="relative z-[1] w-full min-w-0 border-0 bg-transparent [&::-webkit-search-cancel-button]:appearance-none font-apple text-foreground outline-none placeholder:text-muted-foreground"
    style={{ height: expanded ? '100%' : 'var(--qs-search-field-height, 36px)', paddingLeft: expanded ? 0 : 30, paddingRight: (value ? 30 : globalSearch && !expanded ? 48 : 12) + (expanded && filters.length ? 30 : 0), fontSize: 13 }}
  />;
  const clearButton = () => value && <button type="button" aria-label="Clear search"
    onClick={() => { onClear(); setActiveIndex(-1); inputRef.current?.focus(); }}
    className="absolute right-2.5 top-1/2 z-[2] flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full hover:bg-foreground/10"
    style={{ color: 'var(--qs-chrome-ink-dim, var(--muted-foreground))' }}>
    <X aria-hidden="true" className="h-4 w-4" strokeWidth={2.5} />
  </button>;

  return <Popover open={open} onOpenChange={next => next ? show() : close()}>
    <PopoverAnchor asChild>
      <div ref={anchorRef} data-slot="unified-header-search" data-search-scope={searchScope} data-has-value={value ? 'true' : 'false'} className="group relative flex w-full min-w-0 items-center" style={{ width: '100%', pointerEvents: 'auto' }}>
        {/* Painted by globals.css from --qs-search-* tokens, which solid-chrome.css sets. */}
        <span aria-hidden="true" data-search-layer="fill" className="pointer-events-none absolute inset-0" />
        <span aria-hidden="true" data-search-layer="hover" className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-0" />
        <span aria-hidden="true" data-search-layer="focus" className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-150 group-focus-within:opacity-100" />
        <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 z-[2] h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground group-focus-within:text-foreground" strokeWidth={2.25} />
        {!open && <>{renderInput(false)}{clearButton()}{globalSearch && !value && <kbd aria-hidden="true" className="pointer-events-none absolute right-2.5 z-[2] rounded px-1 text-[11px] text-muted-foreground/60">{primaryModifierKeyLabel()}K</kbd>}</>}
      </div>
    </PopoverAnchor>
    <PopoverContent ref={contentRef} surface="solid" align="center" side="bottom" sideOffset={-geometry.height} collisionPadding={{ top: 0, right: 16, bottom: 16, left: 16 }}
      data-desktop-search-popover="true" aria-label={`${ariaLabel} options`} className="flex min-h-0 flex-col p-0"
      style={{ width: geometry.width, maxWidth: 'calc(100vw - 32px)', maxHeight: geometry.availableHeight, '--qs-search-anchor-width': `${geometry.width}px`, '--qs-search-anchor-height': `${geometry.height}px` } as CSSProperties}
      onOpenAutoFocus={event => { event.preventDefault(); restoreInputFocus.current = false; inputRef.current?.focus(); }}
      onCloseAutoFocus={event => {
        event.preventDefault();
        if (restoreInputFocus.current && document.activeElement !== inputRef.current) {
          suppressNextFocus.current = true;
          inputRef.current?.focus();
          suppressNextFocus.current = false;
        }
        restoreInputFocus.current = false;
      }}
      onInteractOutside={event => { if (isAnchorTarget(event.target)) event.preventDefault(); else restoreInputFocus.current = false; }}
      onFocusOutside={event => { if (isAnchorTarget(event.target)) event.preventDefault(); }}
      onEscapeKeyDown={event => { event.preventDefault(); event.stopPropagation(); dismissedEscape.current = event; restoreInputFocus.current = true; close(); }}>
      <div data-desktop-search-cap="true" style={globalSearch ? { width: '100%' } : undefined} className="flex shrink-0 items-center gap-1.5 px-2.5">
        <Search aria-hidden="true" className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
        {renderInput(true)}{clearButton()}
        {filters.length > 0 && <Button variant="ghost" size="icon" className="absolute top-1/2 h-6 w-6 -translate-y-1/2 rounded-full text-inherit hover:bg-foreground/10" style={{ right: value ? 40 : 10 }} aria-label="Search filters" aria-expanded={filterScope === scopeKey} aria-controls={filterId} onClick={() => setFilterScope(previous => previous === scopeKey ? null : scopeKey)}><SlidersHorizontal aria-hidden="true" className="h-3.5 w-3.5" /></Button>}
      </div>
      <div data-desktop-search-neck="true" aria-hidden="true" />
      <div data-desktop-search-tray="true" className="flex min-h-0 flex-col">
      <div data-desktop-search-body="true" className="min-h-0 overflow-y-auto">
      {filterScope === scopeKey && filters.length > 0 && <div id={filterId}><SearchFilters filters={filters} /></div>}
      <div className="px-2 py-3">
        <div ref={suggestionListRef} id={listId} role="listbox" aria-label="Search suggestions">
          {suggestions.map((suggestion, index) => <div key={suggestion.kind === 'ask' ? 'ask' : suggestion.kind !== 'query' ? `${suggestion.kind}:${suggestion.place.id}` : `${suggestion.current ? 'current' : 'recent'}:${suggestion.query}`}>
            {(index === 0 || suggestionGroup(suggestion, !!currentQuery) !== suggestionGroup(suggestions[index - 1], !!currentQuery)) && <p className="mb-1 px-2 text-xs font-medium text-muted-foreground">{suggestionGroup(suggestion, !!currentQuery)}</p>}
            {suggestion.kind === 'ask' ? <button id={`${listId}-${index}`} role="option" data-desktop-search-ask="true" data-default={askDefault && activeIndex < 0 ? 'true' : undefined} aria-selected={activeIndex === index} type="button" tabIndex={-1}
              onMouseDown={event => event.preventDefault()} onMouseEnter={() => setActiveIndex(index)} onClick={() => select(suggestion)}
              className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-accent ${activeIndex === index || (askDefault && activeIndex < 0) ? 'bg-accent' : ''}`}>
              <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/50 text-muted-foreground"><Sparkles className="h-4 w-4" /></span>
              <span data-desktop-search-result-name="true" className="min-w-0 flex-1 truncate">Ask “{suggestion.query}”</span>
              <kbd aria-hidden="true" className="shrink-0 rounded bg-muted px-1 py-0.5 text-xs text-muted-foreground">{askDefault ? 'Enter' : `${primaryModifierKeyLabel()} Enter`}</kbd>
            </button> : <button id={`${listId}-${index}`} role="option" data-desktop-search-result="true" aria-selected={activeIndex === index} type="button" tabIndex={-1}
              onMouseDown={event => event.preventDefault()} onMouseEnter={() => setActiveIndex(index)} onClick={() => select(suggestion)}
              className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-accent ${activeIndex === index ? 'bg-accent' : ''}`}>
              <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/50 text-muted-foreground">{suggestion.kind !== 'query' ? suggestion.place.icon ?? <Clock className="h-4 w-4" /> : suggestion.current ? <Search className="h-4 w-4" /> : <Clock className="h-4 w-4" />}</span>
              <span data-desktop-search-result-name="true" className="min-w-0 flex-1 truncate">{suggestion.kind !== 'query' ? suggestion.place.label : suggestion.current ? `Search for “${suggestion.query}”` : suggestion.query}</span>
              {suggestion.kind !== 'query' && suggestion.place.description && <span data-desktop-search-result-description="true" className="max-w-[45%] truncate text-xs text-muted-foreground">{suggestion.place.description}</span>}
            </button>}
          </div>)}
        </div>
        {globalSearch && currentQuery && (resultsLoading || resultsMessage || !results.length) && <p role="status" className="px-2 py-2 text-sm text-muted-foreground">{resultsLoading ? 'Searching…' : resultsMessage || (moreResults.length ? 'No matches in this batch.' : 'No matching content you can access.')}</p>}
        {currentQuery && moreResults.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{moreResults.map(action => <Button key={action.id} variant="ghost" size="sm" className="h-7 px-2 text-xs text-inherit hover:bg-white/10" disabled={action.disabled || resultsLoading} onClick={action.onSelect}>{action.label}</Button>)}</div>}
        {suggestions.every(suggestion => suggestion.kind === 'ask') && !(globalSearch && currentQuery) && <p role="status" className="px-2 py-2 text-sm text-muted-foreground">{globalSearch ? onAsk ? 'Search projects and tasks, or ask QuantisAI a question.' : 'Search projects and tasks you can access.' : navigationSearch ? currentQuery ? 'No matching destinations.' : 'Search Quantis pages and project sections.' : `Type to search this view${filters.length ? ' or use its filters' : ''}.`}</p>}
      </div>
      </div>
      <div data-desktop-search-footer="true" className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-4 py-2 text-xs text-muted-foreground">
        <span className="shrink-0"><kbd className="rounded bg-muted px-1 py-0.5">↑ ↓</kbd> Choose <span className="ml-3"><kbd>Enter</kbd> Select</span><span className="ml-3"><kbd>Esc</kbd> Close</span></span>
        <div className="flex min-w-0 items-center gap-3"><span className="truncate">{scopeLabel}</span>{history.length > 0 && <Button variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs" onClick={() => { histories.current.delete(scopeKey); refreshHistory(previous => previous + 1); setActiveIndex(-1); inputRef.current?.focus(); }}>Clear recent</Button>}</div>
      </div>
      </div>
    </PopoverContent>
  </Popover>;
}
